import React from "react";
import { DetalleOT, EvidenciaImg } from "../types/ot.types";
import { FileUploaderPreview } from "./FileUploaderPreview";
import {
  getAprobacionBadgeConfig,
  getConceptoCardBorderClass,
  type AprobacionEstado,
} from "../utils/otDetalleExpediente";

interface ExpedienteEvidenciasPorConceptoProps {
  detallesOT: DetalleOT[];
  evidenciasInicio: Record<number, EvidenciaImg[]>;
  evidenciasCierre: Record<number, EvidenciaImg[]>;
}

const noopSetArchivos = () => {};

const AprobacionBadge: React.FC<{
  titulo: string;
  estado: AprobacionEstado;
  motivoRechazo?: string;
}> = ({ titulo, estado, motivoRechazo }) => {
  const config = getAprobacionBadgeConfig(estado);
  return (
    <div className="d-flex flex-column align-items-start">
      <span className={`badge ${config.className}`}>
        <i className={`fas ${config.icon} me-1`}></i>
        {titulo}: {config.label}
      </span>
      {estado === "rechazado" && motivoRechazo && (
        <small className="text-danger mt-1">
          Motivo: {motivoRechazo}
        </small>
      )}
    </div>
  );
};

const EvidenciaPreviewGrupo: React.FC<{
  titulo: string;
  badgeClass: string;
  archivos: EvidenciaImg[];
  omitida?: boolean;
}> = ({ titulo, badgeClass, archivos, omitida = false }) => (
  <div className="h-100">
    <div className="d-flex align-items-center gap-2 mb-2">
      <span className={`badge ${badgeClass}`}>{titulo}</span>
      {!omitida && (
        <span className="text-muted small">{archivos.length} archivo(s)</span>
      )}
    </div>

    {omitida ? (
      <div className="alert alert-secondary py-2 mb-0">
        <i className="fas fa-ban me-2"></i>
        Evidencia de inicio omitida intencionalmente.
      </div>
    ) : (
      <FileUploaderPreview
        label=""
        archivos={archivos}
        setArchivos={noopSetArchivos}
        multiple={true}
        accept="image/*,video/mp4,application/pdf"
        disabled={true}
      />
    )}
  </div>
);

export const ExpedienteEvidenciasPorConcepto: React.FC<
  ExpedienteEvidenciasPorConceptoProps
> = ({ detallesOT, evidenciasInicio, evidenciasCierre }) => {
  if (detallesOT.length === 0) {
    return (
      <div className="alert alert-secondary mb-0">
        No hay conceptos registrados en esta orden de trabajo.
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {detallesOT.map((detalle, idx) => {
        const idOficial = detalle.clave_orden_trabajo_detalle;
        const archivosInicio = evidenciasInicio[idOficial] || [];
        const archivosCierre = evidenciasCierre[idOficial] || [];
        const catLabel = detalle.categoria_label || "Sin categoría";
        const subLabel = detalle.subcategoria_label || "Sin subcategoría";
        const detLabel = detalle.subcategoria_detalle_label || "Sin detalle";
        const sinEvidencia =
          !detalle.evidencia_omitida &&
          archivosInicio.length === 0 &&
          archivosCierre.length === 0;

        return (
          <div
            key={idOficial}
            className={`card shadow-none ${getConceptoCardBorderClass(detalle)}`}
          >
            <div className="card-header py-2 d-flex flex-wrap justify-content-between align-items-start gap-2">
              <div className="flex-grow-1">
                <span className="fw-bold me-2">Concepto #{idx + 1}</span>
                <div className="text-muted small">
                  <strong>{catLabel}</strong> &gt; {subLabel} &gt; {detLabel}
                </div>
                {detalle.observaciones && (
                  <div className="small fst-italic text-secondary mt-1">
                    {detalle.observaciones}
                  </div>
                )}
                <div className="d-flex flex-wrap gap-3 mt-2">
                  <AprobacionBadge
                    titulo="VoBo / Aprobación cliente"
                    estado={detalle.aprobacion_cliente}
                    motivoRechazo={detalle.motivo_rechazo_cliente}
                  />
                  <AprobacionBadge
                    titulo="Aprobación administrativa"
                    estado={detalle.aprobacion_admin}
                    motivoRechazo={detalle.motivo_rechazo_admin}
                  />
                </div>
              </div>
              <div className="d-flex flex-column align-items-end gap-1">
                {sinEvidencia && (
                  <span className="badge bg-warning text-dark">Sin evidencias</span>
                )}
              </div>
            </div>

            <div className="card-body py-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <EvidenciaPreviewGrupo
                    titulo="Inicio de reparación"
                    badgeClass="bg-primary"
                    archivos={archivosInicio}
                    omitida={!!detalle.evidencia_omitida}
                  />
                </div>
                <div className="col-md-6">
                  <EvidenciaPreviewGrupo
                    titulo="Cierre de reparación"
                    badgeClass="bg-success"
                    archivos={archivosCierre}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
